/**
 * Base CRUD Operations - Framework-agnostic
 * Defines the interface for CRUD operations that can be implemented in any framework
 */

/**
 * HTTP method abstraction
 */
export interface HttpClient<T = any> {
  get<R = T>(url: string, options?: any): Promise<R>;
  post<R = T>(url: string, body: any, options?: any): Promise<R>;
  put<R = T>(url: string, body: any, options?: any): Promise<R>;
  delete<R = T>(url: string, options?: any): Promise<R>;
}

/**
 * CRUD endpoints configuration
 */
export interface CrudEndpoints {
  list: string;
  create: string;
  update: string;
  delete: string;
  import?: string;
  export?: string;
}

/**
 * CRUD operation options
 */
export interface CrudOptions {
  showLoading?: boolean;
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

/**
 * Base CRUD Service - Framework-agnostic
 * Provides common CRUD operations that can be wrapped by any framework
 */
export class BaseCrudService<T, TFormData = Partial<T>> {
  constructor(
    protected httpClient: HttpClient,
    protected endpoints: CrudEndpoints,
  ) {}

  /**
   * List/Get all entities
   */
  async list(queryParams?: Record<string, any>): Promise<T[]> {
    let url = this.endpoints.list;

    if (queryParams) {
      const params = new URLSearchParams(queryParams).toString();
      url = `${url}?${params}`;
    }

    return this.httpClient.get<T[]>(url);
  }

  /**
   * Get single entity by ID
   */
  async getById(
    id: number | string,
    queryParams?: Record<string, any>,
  ): Promise<T> {
    let url = `${this.endpoints.list}/${id}`;

    if (queryParams) {
      const params = new URLSearchParams(queryParams).toString();
      url = `${url}?${params}`;
    }

    return this.httpClient.get<T>(url);
  }

  /**
   * Create new entity
   */
  async create(data: TFormData): Promise<T> {
    return this.httpClient.post<T>(this.endpoints.create, data);
  }

  /**
   * Update existing entity
   */
  async update(id: number | string, data: TFormData): Promise<T> {
    const payload = { id, ...data };
    return this.httpClient.put<T>(this.endpoints.update, payload);
  }

  /**
   * Delete entity
   */
  async remove(id: number | string): Promise<void> {
    const payload = { id };
    return this.httpClient.delete(this.endpoints.delete, { body: payload });
  }

  /**
   * Import data from Excel
   */
  async importExcel(data: any): Promise<any> {
    if (!this.endpoints.import) {
      throw new Error("Import endpoint not configured");
    }
    return this.httpClient.post(this.endpoints.import, data);
  }

  /**
   * Export data to Excel
   */
  async exportExcel(filters?: Record<string, any>): Promise<any> {
    if (!this.endpoints.export) {
      throw new Error("Export endpoint not configured");
    }

    let url = this.endpoints.export;
    if (filters) {
      const params = new URLSearchParams(filters).toString();
      url = `${url}?${params}`;
    }

    return this.httpClient.get(url);
  }

  /**
   * Batch operations
   */
  async batchCreate(items: TFormData[]): Promise<T[]> {
    return Promise.all(items.map((item) => this.create(item)));
  }

  async batchUpdate(
    items: Array<{ id: number | string; data: TFormData }>,
  ): Promise<T[]> {
    return Promise.all(items.map((item) => this.update(item.id, item.data)));
  }

  async batchRemove(ids: Array<number | string>): Promise<void[]> {
    return Promise.all(ids.map((id) => this.remove(id)));
  }
}

/**
 * CRUD Service Factory
 * Creates a CRUD service instance for a specific entity
 */
export function createCrudService<T, TFormData = Partial<T>>(
  httpClient: HttpClient,
  endpoints: CrudEndpoints,
): BaseCrudService<T, TFormData> {
  return new BaseCrudService<T, TFormData>(httpClient, endpoints);
}
